import { internalData } from '../../../store/store.ts';
import { iconPath } from '../../../data/icons.ts';
import { devlog } from '../../../util/utils.ts';
import '../../text-and-lang/as-text.ts';
import '../../overlays/as-tooltip-wrapper.ts';
import '../../base/as-spinner.ts';
import { CONFIRM_ON_CLICK_ATTRS, confirmOnClickSheets, wrapWithConfirm, attachConfirmListener } from './confirmOnClick.ts';
import styleText from './as-button.css?raw';
import { AsButtonBase } from './as-button-base.ts';

/**
 * <as-button>
 *
 * Combined icon + text button. Either or both may be supplied; when only an
 * icon is provided the button renders square (icon-only mode).
 *
 * Attributes
 *   abs-img-path   Absolute path to icon image (highest priority)
 *   icon-set       Icon set name (see DEFAULT_ICON_LIST in data/icons)
 *   icon-variant   Variant within icon-set (default: "default")
 *   file-name      Filename relative to commonAppData.imgDir (lowest priority)
 *   black          When present, applies brightness(0) filter to icon
 *   default-text              Button label text
 *   dict-key                  Localization key for button label
 *   toggle-on-default-text    Label text shown when toggled on (requires toggle attribute)
 *   toggle-on-dict-key        Localization key for toggle-on label
 *   name           Identifies the button in as-button-click events
 *   button-id      Sets the id on the inner <button> element
 *   disabled       Disables the button
 *   pending        Shows a spinner instead of icon/text and disables the button
 *   pending-default-text   Label shown next to the spinner while pending (e.g. "Cancelling…").
 *                          Without it, pending is spinner-only.
 *   pending-dict-key       Localization key for the pending label
 *   toggle         When present, button toggles on/off state on each click
 *   link           href for a real, connected, light-DOM anchor overlaid on the
 *                  button (see AsButtonBase._syncLinkOverlay) — required for hosts
 *                  like GrabCAD that only intercept genuine anchor clicks
 *   link-new-tab   When present, the link anchor opens in a new tab
 *   tooltip-default-text   Tooltip text
 *   tooltip-dict-key       Tooltip localization key
 *   tooltip-placement      Tooltip placement (default: "bottom")
 *   disable-tooltips       Suppress tooltip rendering
 *
 * Events
 *   as-button-click  CustomEvent<{ buttonName, toggledOn, originalEvent }>  bubbles, composed
 *
 * CSS custom properties
 *   --as-btn-gap, --as-btn-flex-direction, --as-btn-bg, --as-btn-hover-bg, --as-btn-color, --as-btn-hover-color
 *   --as-btn-padding, --as-btn-padding-x, --as-btn-padding-y, --as-btn-radius
 *   --as-btn-height, --as-btn-min-width, --as-btn-max-width
 *   --as-btn-font-size, --as-btn-font-weight
 *   --as-btn-img-size, --as-btn-img-hover-filter, --as-btn-img-filter
 *   --as-btn-on-color, --as-btn-focus-color
 */
export class AsButton extends AsButtonBase {
  static readonly tag = 'as-button' as const;

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(styleText);
    return s;
  })();

  static get observedAttributes(): string[] {
    return [
      'abs-img-path', 'file-name', 'icon-set', 'icon-variant', 'black',
      'default-text', 'dict-key', 'toggle-on-default-text', 'toggle-on-dict-key',
      'name', 'button-id', 'disabled', 'pending', 'pending-default-text', 'pending-dict-key',
      'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement', 'disable-tooltips',
      ...CONFIRM_ON_CLICK_ATTRS,
      'toggle', 'link', 'link-new-tab',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, ...confirmOnClickSheets, AsButton._sheet];
    this._render();
  }

  connectedCallback(): void {
    if (this.getAttribute('toggle') === 'on') {
      this.toggledOn = true;
    }
    this._syncToggleState();
    this._attachListeners();
    this._syncLinkOverlay();
  }

  attributeChangedCallback(name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    if (name === 'toggle') {
      this._syncToggleState();
    } else if (name.startsWith('confirm-')) {
      this._render();
    } else if (name !== 'link' && name !== 'link-new-tab') {
      this._render();
      this._attachListeners();
    }
    this._syncLinkOverlay();
  }

  // ---- Private ----

  private _getIconSrc(): string {
    const absPath = this.getAttribute('abs-img-path');
    if (absPath) return absPath;

    const iconSet     = this.getAttribute('icon-set');
    const iconVariant = this.getAttribute('icon-variant') ?? 'default';

    const fromSet = iconPath(iconSet, iconVariant, true);
    if (fromSet) return fromSet;

    const name = this.getAttribute('name');
    if (!iconSet && !this.hasAttribute('no-default-icon')) {
      const fromName = iconPath(name, iconVariant);
      if (fromName) return fromName;
    }

    const fileName = this.getAttribute('file-name');
    if (fileName) return `${internalData.imgDir}/${fileName}`;

    return '';
  }

  private _hasTooltip(): boolean {
    return !!(
      (this.getAttribute('tooltip-dict-key') || this.getAttribute('tooltip-default-text')) &&
      !this.hasAttribute('disable-tooltips')
    );
  }

  private _render(): void {
    const iconSrc = this._getIconSrc();
    const hasIcon = !!iconSrc;
    const useToggleOn = this.toggledOn && this.hasAttribute('toggle');
    const text    = (useToggleOn && this.hasAttribute('toggle-on-default-text'))
      ? (this.getAttribute('toggle-on-default-text') ?? '')
      : (this.getAttribute('default-text') ?? '');
    const dictKey = (useToggleOn && this.hasAttribute('toggle-on-dict-key'))
      ? this.getAttribute('toggle-on-dict-key')
      : this.getAttribute('dict-key');
    const hasText  = !!(text || dictKey);

    const isPending      = this.hasAttribute('pending');
    const pendingText    = this.getAttribute('pending-default-text') ?? '';
    const pendingDictKey = this.getAttribute('pending-dict-key');
    // A pending button is spinner-only unless it was given its own label.
    const hasPendingText = isPending && !!(pendingText || pendingDictKey);

    // Host class for icon-only square sizing
    this.classList.toggle('icon-only', hasIcon && !hasText && !hasPendingText);

    const imgHTML = hasIcon
      ? `<img class="btn-img" src="${iconSrc}" alt="">`
      : '';

    const textEl = (label: string, key: string | null): string => key
      ? `<as-text class="btn-text" dict-key="${key}" default-text="${label}"></as-text>`
      : `<as-text class="btn-text">${label}</as-text>`;

    const textHTML = hasText ? textEl(text, dictKey) : '';

    const isDisabled = this.hasAttribute('disabled') || isPending;
    const buttonId   = this.getAttribute('button-id') ?? '';

    const contentHTML = isPending
      ? `<as-spinner class="btn-spinner"></as-spinner>${hasPendingText ? textEl(pendingText, pendingDictKey) : ''}`
      : `${imgHTML}${textHTML}`;
    let buttonHTML = `<button id="${buttonId}"${isDisabled ? ' disabled' : ''}>${contentHTML}</button>`;

    if (this._hasTooltip()) {
      const placement = this.getAttribute('tooltip-placement') ?? 'bottom';
      const ttKey  = this.getAttribute('tooltip-dict-key');
      const ttText = this.getAttribute('tooltip-default-text');
      const attrs  = [`show-on-hover`, `placement="${placement}"`];
      if (ttKey)  attrs.push(`dict-key="${ttKey}"`);
      if (ttText) attrs.push(`default-text="${ttText}"`);
      buttonHTML = `<as-tooltip-wrapper ${attrs.join(' ')}>${buttonHTML}</as-tooltip-wrapper>`;
    }

    buttonHTML = wrapWithConfirm(this, buttonHTML);

    this.shadowRoot!.innerHTML = buttonHTML + '<slot name="link-overlay"></slot>';
    this._attachListeners();
  }

  private _attachListeners(): void {
    const button = this.shadowRoot?.querySelector('button');
    if (button) {
      button.removeEventListener('click', this._handleClick);
      button.addEventListener('click', this._handleClick);
    }

    attachConfirmListener(this, () => this._dispatchConfirmedClick());
  }

  private _handleClick = (e: MouseEvent): void => {
    if (this.hasAttribute('confirm-on-click')) {
      // as-hovercard-wrapper handles the click to show confirmation
      return;
    }

    if (this.hasAttribute('toggle')) {
      this.toggledOn = !this.toggledOn;
      this._syncToggleState();
    }
    devlog('AsButton: as-button-click', this.getAttribute('name'));
    this._dispatchButtonClick({
      buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      toggledOn:  this.toggledOn,
      originalEvent: e,
    });
    // Fallback for when the `link` overlay anchor ends up zero-size and the
    // click lands on this shadow button instead — see AsButtonBase._navigateLink.
    this._navigateLink(e);
  };

  private _dispatchConfirmedClick() {
    this._dispatchButtonClick({
      buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      toggledOn:  this.toggledOn
    });
  }

  protected _syncToggleState(): void {
    if (this.hasAttribute('toggle') && (this.hasAttribute('toggle-on-default-text') || this.hasAttribute('toggle-on-dict-key'))) {
      this._render();
    }
    const button = this.shadowRoot?.querySelector('button');
    if (!button) return;
    if (this.hasAttribute('toggle')) {
      button.classList.toggle('on', this.toggledOn);
    } else {
      button.classList.remove('on');
    }
  }
}

if (!customElements.get(AsButton.tag)) {
  customElements.define(AsButton.tag, AsButton);
} else {
  console.warn('as-button already defined');
}
